import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Briefcase, MapPin, Euro, LogOut, User, Home, ClipboardList, Trash2, X, Edit2, Filter, Clock, Tag, Loader2, Camera, Sparkles, ThumbsUp, ThumbsDown } from 'lucide-react';
import { AUSTRIAN_STATES, getDistrictsForState, getGemeindenForDistrict, getLocationCoordinates } from '../data/austrianLocations';
import { ResidenceLocationFields } from '../components/profile/ResidenceLocationFields';
import { PreferredWorkLocationsField } from '../components/profile/PreferredWorkLocationsField';
import { JobAreasField } from '../components/profile/JobAreasField';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function StudentDashboard({ user, onLogout, setUser }) {
  // Prevent search engine indexing of student area
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => document.head.removeChild(meta);
  }, []);

  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('jobs');
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const profileImageInputRef = useRef(null);
  const [loadingCategories, setLoadingCategories] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    minSalary: '',
    maxSalary: '',
    minHours: '',
    maxHours: '',
    employmentType: '',
    state: ''
  });
  
  // Edit profile states
  const [editData, setEditData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    age: '',
    gender: '',
    study: '',
    residenceState: '',
    residenceCity: '',
    residenceGemeinde: '',
    residenceAddress: '',
    hasDriverLicense: false,
    hasOwnCar: false,
    availableHoursPerWeek: '',
    timeConstraints: '',
    preferredStates: [],
    preferredDistricts: [],
    preferredWorkLocations: [], // New: Array of {state, district} for detailed work locations
    preferredJobAreas: [],
    languageSkills: []
  });
  const [newLanguage, setNewLanguage] = useState({ language: '', level: '' });
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 768 : false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    applyFiltersAndSort();
  }, [jobs, filters, user]);

  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      const response = await axios.get(`${API}/job-categories`);
      setAvailableCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories');
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchData = async () => {
    try {
      const [jobsRes, appsRes, propRes] = await Promise.all([
        axios.get(`${API}/jobs`),
        axios.get(`${API}/applications`),
        axios.get(`${API}/me/proposals`)
      ]);
      setJobs(jobsRes.data);
      // Jobs werden in applyFiltersAndSort sortiert
      setApplications(appsRes.data);
      setProposals(propRes.data || []);
    } catch (error) {
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleProposalResponse = async (proposalId, response) => {
    try {
      await axios.patch(`${API}/me/proposals/${proposalId}/respond`, { response });
      toast.success(response === 'interested' ? 'Super, Sabine wurde informiert! 🎉' : 'Vorschlag abgesagt');
      // Refresh proposals
      const propRes = await axios.get(`${API}/me/proposals`);
      setProposals(propRes.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Antworten');
    }
  };

  const applyFiltersAndSort = () => {
    let filtered = [...jobs];
    
    // Filters anwenden
    if (filters.minSalary) {
      filtered = filtered.filter(job => (job.salaryBrutto || job.monthlySalary) >= parseInt(filters.minSalary));
    }
    if (filters.maxSalary) {
      filtered = filtered.filter(job => (job.salaryBrutto || job.monthlySalary) <= parseInt(filters.maxSalary));
    }
    if (filters.minHours) {
      filtered = filtered.filter(job => job.hoursPerWeek >= parseInt(filters.minHours));
    }
    if (filters.maxHours) {
      filtered = filtered.filter(job => job.hoursPerWeek <= parseInt(filters.maxHours));
    }
    if (filters.employmentType) {
      filtered = filtered.filter(job => job.employmentType === filters.employmentType);
    }
    if (filters.state) {
      filtered = filtered.filter(job => job.state === filters.state);
    }
    
    // Job-Matching: Sortierung nach Relevanz
    const userResidenceState = user?.profile?.residenceState;
    const userPreferredStates = user?.profile?.preferredStates || [];
    const userPreferredAreas = user?.profile?.preferredJobAreas || [];
    
    // Berechne Score für jeden Job
    const scoredJobs = filtered.map(job => {
      let score = 0;
      
      // Höchste Priorität: Job im gleichen Bundesland wie Wohnort
      if (userResidenceState && job.state === userResidenceState) {
        score += 100;
      }
      
      // Zweite Priorität: Job in bevorzugtem Bundesland
      if (userPreferredStates.includes(job.state)) {
        score += 50;
      }
      
      // Dritte Priorität: Übereinstimmung mit Interessen/Kategorien
      const jobCategories = job.categories || [];
      const matchingCategories = jobCategories.filter(cat => userPreferredAreas.includes(cat));
      score += matchingCategories.length * 20;
      
      return { ...job, matchScore: score };
    });
    
    // Sortiere nach Score (höchster zuerst)
    scoredJobs.sort((a, b) => b.matchScore - a.matchScore);
    
    setFilteredJobs(scoredJobs);
  };

  const clearFilters = () => {
    setFilters({
      minSalary: '',
      maxSalary: '',
      minHours: '',
      maxHours: '',
      employmentType: '',
      state: ''
    });
  };

  const handleApply = async (jobId) => {
    try {
      await axios.post(`${API}/applications`, { jobId });
      toast.success('Bewerbung erfolgreich!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Bewerben');
    }
  };

  const handleWithdrawApplication = async (appId) => {
    try {
      await axios.delete(`${API}/applications/${appId}`);
      toast.success('Bewerbung zurückgezogen');
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Zurückziehen der Bewerbung');
    }
  };

  const handleDeleteAccount = async () => {
    try {
      await axios.delete(`${API}/users/account`);
      toast.success('Konto gelöscht');
      onLogout();
    } catch (error) {
      toast.error('Fehler beim Löschen des Kontos');
    }
  };

  const handleSaveProfile = async () => {
    try {
      // Calculate coordinates for proximity sorting
      const coords = getLocationCoordinates(
        editData.residenceState, 
        editData.residenceCity, 
        editData.residenceGemeinde
      );
      
      const dataToSave = {
        ...editData,
        latitude: coords?.lat || null,
        longitude: coords?.lng || null
      };
      
      console.log('Saving profile data:', dataToSave);
      const response = await axios.patch(`${API}/users/profile`, dataToSave);
      toast.success('Profil aktualisiert!');
      setUser(response.data);
      setShowEditProfile(false);
    } catch (error) {
      console.error('Profile save error:', error.response?.data || error.message);
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const handleProfileImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Bitte wähle ein Bild aus');
      return;
    }

    setUploadingImage(true);
    
    try {
      // Resize image
      const reader = new FileReader();
      reader.onload = async (event) => {
        const img = new Image();
        img.onload = async () => {
          const canvas = document.createElement('canvas');
          const MAX_SIZE = 500;
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > MAX_SIZE) {
              height *= MAX_SIZE / width;
              width = MAX_SIZE;
            }
          } else {
            if (height > MAX_SIZE) {
              width *= MAX_SIZE / height;
              height = MAX_SIZE;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          const resizedBase64 = canvas.toDataURL('image/jpeg', 0.8);
          
          // Upload to server
          const response = await axios.post(`${API}/users/upload-profile-image`, {
            image: resizedBase64
          });
          
          setUser(response.data);
          toast.success('Profilbild aktualisiert!');
          setUploadingImage(false);
        };
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Fehler beim Hochladen');
      setUploadingImage(false);
    }
  };

  const toggleJobArea = (area) => {
    setEditData(prev => ({
      ...prev,
      preferredJobAreas: prev.preferredJobAreas.includes(area)
        ? prev.preferredJobAreas.filter(a => a !== area)
        : [...prev.preferredJobAreas, area]
    }));
  };

  const hasApplied = (jobId) => {
    return applications.some(app => app.jobId === jobId);
  };

  const getApplicationStatus = (jobId) => {
    const app = applications.find(app => app.jobId === jobId);
    return app?.status || 'beworben';
  };

  const isInPreferredRegion = (job) => {
    const userStates = user.profile?.preferredStates || [];
    const userDistricts = user.profile?.preferredDistricts || [];
    return userStates.includes(job.state) || userDistricts.includes(job.district);
  };

  const activeFiltersCount = Object.values(filters).filter(v => v !== '').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-white pb-20 md:pb-6">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-xl border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl md:text-2xl font-bold font-outfit text-slate-900">
              Hallo, {user.profile?.firstName || 'Student'}! 👋
            </h1>
            <p className="text-sm text-slate-600">{user.profile?.study || 'Student'}</p>
          </div>
          {!isMobile && (
            <Button
              onClick={onLogout}
              variant="ghost"
              size="icon"
              className="rounded-full"
              data-testid="logout-button"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          )}
        </div>
      </div>

      {/* Desktop Navigation */}
      {!isMobile && (
        <div className="bg-white border-b border-slate-200">
          <div className="max-w-6xl mx-auto px-4">
            <div className="flex gap-4">
              <button
                onClick={() => setActiveTab('jobs')}
                className={`py-4 px-6 border-b-2 transition-colors ${
                  activeTab === 'jobs'
                    ? 'border-primary text-primary font-medium'
                    : 'border-transparent text-slate-600 hover:text-slate-900'
                }`}
                data-testid="tab-jobs"
              >
                <Home className="w-4 h-4 inline-block mr-2" />
                Jobs
              </button>
              <button
                onClick={() => setActiveTab('applications')}
                className={`py-4 px-6 border-b-2 transition-colors relative ${
                  activeTab === 'applications'
                    ? 'border-primary text-primary font-medium'
                    : 'border-transparent text-slate-600 hover:text-slate-900'
                }`}
                data-testid="tab-applications"
              >
                <ClipboardList className="w-4 h-4 inline-block mr-2" />
                Bewerbungen
                {proposals.filter(p => p.status === 'pending').length > 0 && (
                  <span className="absolute -top-0.5 right-1 bg-amber-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5">
                    {proposals.filter(p => p.status === 'pending').length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('profile')}
                className={`py-4 px-6 border-b-2 transition-colors ${
                  activeTab === 'profile'
                    ? 'border-primary text-primary font-medium'
                    : 'border-transparent text-slate-600 hover:text-slate-900'
                }`}
                data-testid="tab-profile"
              >
                <User className="w-4 h-4 inline-block mr-2" />
                Profil
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sabine Begrüßungs-Banner */}
      <div className="bg-gradient-to-r from-primary/5 to-secondary/5 border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <img
              src="https://customer-assets.emergentagent.com/job_16c51e6b-b780-4d95-b9ce-c1cedd6ce457/artifacts/hr2ssmdj_sabinenotvotny.png"
              alt="Sabine Novotny - Deine Ansprechpartnerin bei UniSide"
              className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover border-3 border-white shadow-lg flex-shrink-0"
            />
            <div className="flex-1">
              <p className="text-sm md:text-base text-slate-700 leading-relaxed">
                <strong className="text-primary">Hallo, ich bin Sabine!</strong> Sobald du einen passenden Job für dich entdeckt hast, 
                melde ich mich bei dir, damit wir uns kurz kennenlernen können. Ich betreue dich gerne und 
                <span className="font-semibold text-green-600"> kostenlos</span> – und verbinde dich direkt mit den Entscheidern. Bis bald! 👋
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        {activeTab === 'jobs' && (
          <div className="space-y-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold font-outfit">Verfügbare Jobs</h2>
                <p className="text-sm text-slate-600">{filteredJobs.length} von {jobs.length} Jobs</p>
              </div>
              <Button
                onClick={() => setShowFilters(!showFilters)}
                variant="outline"
                className="rounded-full"
                data-testid="toggle-filters"
              >
                <Filter className="w-4 h-4 mr-2" />
                Filter {activeFiltersCount > 0 && `(${activeFiltersCount})`}
              </Button>
            </div>

            {/* Filter Panel */}
            {showFilters && (
              <Card className="border-slate-200 rounded-2xl">
                <CardContent className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Min. Gehalt (€)</Label>
                      <Input
                        type="number"
                        placeholder="z.B. 600"
                        value={filters.minSalary}
                        onChange={(e) => setFilters({...filters, minSalary: e.target.value})}
                        className="rounded-xl"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Max. Gehalt (€)</Label>
                      <Input
                        type="number"
                        placeholder="z.B. 1500"
                        value={filters.maxSalary}
                        onChange={(e) => setFilters({...filters, maxSalary: e.target.value})}
                        className="rounded-xl"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Bundesland</Label>
                      <Select value={filters.state} onValueChange={(value) => setFilters({...filters, state: value})}>
                        <SelectTrigger className="rounded-xl">
                          <SelectValue placeholder="Alle" />
                        </SelectTrigger>
                        <SelectContent className="z-[100]">
                          <SelectItem value="">Alle</SelectItem>
                          {AUSTRIAN_STATES.map(state => (
                            <SelectItem key={state} value={state}>{state}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Min. Stunden/Woche</Label>
                      <Input
                        type="number"
                        placeholder="z.B. 10"
                        value={filters.minHours}
                        onChange={(e) => setFilters({...filters, minHours: e.target.value})}
                        className="rounded-xl"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Max. Stunden/Woche</Label>
                      <Input
                        type="number"
                        placeholder="z.B. 20"
                        value={filters.maxHours}
                        onChange={(e) => setFilters({...filters, maxHours: e.target.value})}
                        className="rounded-xl"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Beschäftigungsart</Label>
                      <Select value={filters.employmentType} onValueChange={(value) => setFilters({...filters, employmentType: value})}>
                        <SelectTrigger className="rounded-xl">
                          <SelectValue placeholder="Alle" />
                        </SelectTrigger>
                        <SelectContent className="z-[100]">
                          <SelectItem value="">Alle</SelectItem>
                          <SelectItem value="Geringfügig">Geringfügig</SelectItem>
                          <SelectItem value="Teilzeit">Teilzeit</SelectItem>
                          <SelectItem value="Vollzeit">Vollzeit</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button onClick={clearFilters} variant="outline" className="rounded-full">
                      Filter zurücksetzen
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Jobs Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredJobs.map((job) => (
                <Card
                  key={job.jobId}
                  className="border-slate-100 shadow-sm hover:shadow-md transition-shadow rounded-2xl"
                  data-testid={`job-card-${job.jobId}`}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <CardTitle className="font-outfit text-lg leading-tight mb-2">
                          {job.title}
                        </CardTitle>
                        <div className="flex flex-wrap gap-2">
                          <Badge variant="secondary" className="bg-slate-100 text-slate-700 text-xs">
                            {job.employmentType}
                          </Badge>
                          {job.isDemo && (
                            <Badge variant="outline" className="text-xs">DEMO</Badge>
                          )}
                          {isInPreferredRegion(job) && (
                            <Badge className="bg-secondary text-secondary-foreground text-xs">
                              In deiner Nähe
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-sm text-slate-600 line-clamp-2">{job.description}</p>
                    
                    {/* Job Categories */}
                    {job.categories?.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {job.categories.slice(0, 2).map((cat) => (
                          <Badge key={cat} variant="outline" className="text-xs bg-primary/5 text-primary border-primary/20">
                            {cat}
                          </Badge>
                        ))}
                        {job.categories.length > 2 && (
                          <Badge variant="outline" className="text-xs">
                            +{job.categories.length - 2}
                          </Badge>
                        )}
                      </div>
                    )}
                    
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="flex items-center gap-1 text-slate-600">
                        <MapPin className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate">{job.location}</span>
                      </div>
                      <div className="flex items-center gap-1 text-slate-600">
                        <Clock className="w-4 h-4 flex-shrink-0" />
                        <span>{job.hoursPerWeek}h/Woche</span>
                      </div>
                    </div>

                    {typeof job.distanceKm === 'number' && (
                      <div
                        className="inline-flex items-center gap-1 text-xs font-medium text-primary bg-primary/5 px-2.5 py-1 rounded-full w-fit"
                        data-testid={`job-distance-${job.jobId}`}
                      >
                        <MapPin className="w-3 h-3" />
                        {job.distanceKm < 1
                          ? 'Weniger als 1 km entfernt'
                          : `${job.distanceKm.toFixed(1).replace('.', ',')} km entfernt`}
                      </div>
                    )}
                    
                    {job.workingHours && (
                      <div className="text-xs text-slate-500 bg-slate-50 px-3 py-2 rounded-lg">
                        Arbeitszeiten: {job.workingHours}
                      </div>
                    )}
                    
                    <div className="flex items-center gap-1 text-primary font-bold text-lg">
                      <Euro className="w-5 h-5" />
                      <span>€{job.salaryBrutto || job.monthlySalary} brutto/Monat</span>
                    </div>

                    {hasApplied(job.jobId) ? (
                      <Button
                        disabled
                        className="w-full h-11 rounded-full"
                        variant={getApplicationStatus(job.jobId) === 'zugesagt' ? 'default' : 'outline'}
                        data-testid={`application-status-${job.jobId}`}
                      >
                        {getApplicationStatus(job.jobId) === 'beworben' && '✓ Beworben'}
                        {getApplicationStatus(job.jobId) === 'zugesagt' && '🎉 Zusage erhalten!'}
                        {getApplicationStatus(job.jobId) === 'abgelehnt' && 'Abgelehnt'}
                      </Button>
                    ) : (
                      <Button
                        onClick={() => handleApply(job.jobId)}
                        className="w-full h-11 rounded-full bg-primary hover:bg-primary/90 shadow-lg"
                        data-testid={`apply-button-${job.jobId}`}
                      >
                        <Briefcase className="mr-2 w-4 h-4" />
                        Jetzt bewerben
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>

            {filteredJobs.length === 0 && (
              <div className="text-center py-12">
                <Briefcase className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600">Keine Jobs gefunden</p>
                <Button onClick={clearFilters} variant="outline" className="mt-4 rounded-full">
                  Filter zurücksetzen
                </Button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'applications' && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold font-outfit mb-4">Meine Bewerbungen</h2>

            {/* Job Proposals from Sabine - highlighted */}
            {proposals.filter(p => p.status === 'pending').length > 0 && (
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-500" />
                  <h3 className="text-lg font-bold font-outfit text-amber-900">
                    Job-Vorschläge für dich ({proposals.filter(p => p.status === 'pending').length})
                  </h3>
                </div>
                {proposals.filter(p => p.status === 'pending').map((proposal) => {
                  const job = proposal.job || {};
                  const daysLeft = proposal.expiresAt
                    ? Math.max(0, Math.ceil((new Date(proposal.expiresAt) - new Date()) / (1000 * 60 * 60 * 24)))
                    : null;
                  return (
                    <Card
                      key={proposal.proposalId}
                      className="border-amber-300 border-2 shadow-lg rounded-2xl bg-gradient-to-br from-amber-50 to-orange-50"
                      data-testid={`proposal-card-${proposal.proposalId}`}
                    >
                      <CardHeader>
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="flex-1 min-w-0">
                            <Badge className="bg-amber-500 text-white mb-2">
                              <Sparkles className="w-3 h-3 mr-1" /> Vorschlag von Sabine
                            </Badge>
                            <CardTitle className="font-outfit text-lg">{job.title || 'Job-Vorschlag'}</CardTitle>
                            <CardDescription className="flex items-center gap-1 mt-1">
                              <MapPin className="w-3.5 h-3.5" />
                              {job.location || '—'}
                            </CardDescription>
                          </div>
                          {daysLeft !== null && (
                            <Badge variant="outline" className="border-amber-400 text-amber-700">
                              <Clock className="w-3 h-3 mr-1" />
                              Noch {daysLeft} {daysLeft === 1 ? 'Tag' : 'Tage'}
                            </Badge>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {job.description && (
                          <p className="text-sm text-slate-700 line-clamp-3">{job.description}</p>
                        )}
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div className="flex items-center gap-1 text-slate-700">
                            <Euro className="w-4 h-4 text-primary" />
                            <span className="font-semibold">€{job.salaryBrutto || job.monthlySalary || '—'} brutto</span>
                          </div>
                          <div className="flex items-center gap-1 text-slate-700">
                            <Clock className="w-4 h-4" />
                            {job.hoursPerWeek || '—'}h/Woche
                          </div>
                        </div>
                        {proposal.message && (
                          <div className="bg-white/70 border-l-4 border-amber-400 p-3 rounded-r-lg text-sm text-slate-700">
                            <span className="font-medium text-amber-800">💬 Sabines Notiz:</span> {proposal.message}
                          </div>
                        )}
                        <div className="flex gap-2 pt-2">
                          <Button
                            onClick={() => handleProposalResponse(proposal.proposalId, 'interested')}
                            className="flex-1 rounded-full bg-green-600 hover:bg-green-700 text-white"
                            data-testid={`proposal-interested-${proposal.proposalId}`}
                          >
                            <ThumbsUp className="w-4 h-4 mr-2" />
                            Interessant
                          </Button>
                          <Button
                            onClick={() => handleProposalResponse(proposal.proposalId, 'declined')}
                            variant="outline"
                            className="flex-1 rounded-full border-red-300 text-red-600 hover:bg-red-50"
                            data-testid={`proposal-declined-${proposal.proposalId}`}
                          >
                            <ThumbsDown className="w-4 h-4 mr-2" />
                            Absagen
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* Already-responded proposals (compact list) */}
            {proposals.filter(p => p.status !== 'pending').length > 0 && (
              <div className="space-y-2 mb-6">
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">
                  Frühere Vorschläge
                </h3>
                {proposals.filter(p => p.status !== 'pending').map((proposal) => (
                  <div
                    key={proposal.proposalId}
                    className="flex items-center justify-between bg-slate-50 rounded-xl px-4 py-2 text-sm"
                    data-testid={`proposal-history-${proposal.proposalId}`}
                  >
                    <span className="font-medium text-slate-700 truncate">
                      {proposal.job?.title || 'Job'}
                    </span>
                    <Badge
                      className={
                        proposal.status === 'interested' ? 'bg-green-100 text-green-700' :
                        proposal.status === 'declined' ? 'bg-red-100 text-red-700' :
                        'bg-slate-200 text-slate-600'
                      }
                    >
                      {proposal.status === 'interested' && 'Zugesagt'}
                      {proposal.status === 'declined' && 'Abgelehnt'}
                      {proposal.status === 'expired' && 'Abgelaufen'}
                    </Badge>
                  </div>
                ))}
              </div>
            )}

            {applications.map((app) => {
              const job = jobs.find(j => j.jobId === app.jobId);
              if (!job) return null;

              return (
                <Card
                  key={app.applicationId}
                  className="border-slate-100 shadow-sm rounded-2xl"
                  data-testid={`application-card-${app.applicationId}`}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="font-outfit text-lg">{job.title}</CardTitle>
                        <CardDescription>
                          Beworben am {new Date(app.appliedAt).toLocaleDateString('de-AT')}
                        </CardDescription>
                      </div>
                      {app.status === 'beworben' && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`withdraw-app-${app.applicationId}`}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Bewerbung zurückziehen?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Möchtest du deine Bewerbung für "{job.title}" wirklich zurückziehen?
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleWithdrawApplication(app.applicationId)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Zurückziehen
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Badge
                      className={
                        app.status === 'zugesagt'
                          ? 'bg-green-100 text-green-700'
                          : app.status === 'abgelehnt'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-orange-100 text-orange-700'
                      }
                    >
                      {app.status === 'beworben' && 'In Bearbeitung'}
                      {app.status === 'zugesagt' && 'Zusage'}
                      {app.status === 'abgelehnt' && 'Abgelehnt'}
                    </Badge>
                  </CardContent>
                </Card>
              );
            })}

            {applications.length === 0 && proposals.length === 0 && (
              <div className="text-center py-12">
                <ClipboardList className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600">Noch keine Bewerbungen</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'profile' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold font-outfit">Mein Profil</h2>
              <Dialog open={showEditProfile} onOpenChange={(open) => {
                setShowEditProfile(open);
                if (open) fetchCategories();
              }}>
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    className="rounded-full"
                    onClick={() => {
                      setEditData({
                        firstName: user.profile?.firstName || '',
                        lastName: user.profile?.lastName || '',
                        phone: user.profile?.phone || '',
                        age: user.profile?.age || '',
                        gender: user.profile?.gender || '',
                        study: user.profile?.study || '',
                        residenceState: user.profile?.residenceState || '',
                        residenceCity: user.profile?.residenceCity || '',
                        residenceGemeinde: user.profile?.residenceGemeinde || '',
                        residenceAddress: user.profile?.residenceAddress || '',
                        hasDriverLicense: user.profile?.hasDriverLicense || false,
                        hasOwnCar: user.profile?.hasOwnCar || false,
                        availableHoursPerWeek: user.profile?.availableHoursPerWeek || '',
                        timeConstraints: user.profile?.timeConstraints || '',
                        preferredStates: user.profile?.preferredStates || [],
                        preferredDistricts: user.profile?.preferredDistricts || [],
                        preferredWorkLocations: user.profile?.preferredWorkLocations || [],
                        preferredJobAreas: user.profile?.preferredJobAreas || [],
                        languageSkills: user.profile?.languageSkills || []
                      });
                    }}
                    data-testid="edit-profile-button"
                  >
                    <Edit2 className="w-4 h-4 mr-2" />
                    Bearbeiten
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto w-[95vw] sm:w-full">
                  <DialogHeader>
                    <DialogTitle>Profil bearbeiten</DialogTitle>
                    <DialogDescription>
                      Ändere deine persönlichen Informationen
                    </DialogDescription>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    {/* Persönliche Daten */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Vorname</Label>
                        <Input
                          value={editData.firstName}
                          onChange={(e) => setEditData({...editData, firstName: e.target.value})}
                          data-testid="edit-firstname"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Nachname</Label>
                        <Input
                          value={editData.lastName}
                          onChange={(e) => setEditData({...editData, lastName: e.target.value})}
                          data-testid="edit-lastname"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Alter</Label>
                        <Input
                          type="number"
                          value={editData.age}
                          onChange={(e) => setEditData({...editData, age: parseInt(e.target.value) || ''})}
                          data-testid="edit-age"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Geschlecht</Label>
                        <Select
                          value={editData.gender}
                          onValueChange={(value) => setEditData({...editData, gender: value})}
                        >
                          <SelectTrigger data-testid="edit-gender">
                            <SelectValue placeholder="Wählen..." />
                          </SelectTrigger>
                          <SelectContent className="z-[100]">
                            <SelectItem value="männlich">Männlich</SelectItem>
                            <SelectItem value="weiblich">Weiblich</SelectItem>
                            <SelectItem value="divers">Divers</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Telefon</Label>
                      <Input
                        value={editData.phone}
                        onChange={(e) => setEditData({...editData, phone: e.target.value})}
                        placeholder="+43 660 1234567"
                        data-testid="edit-phone"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Studium</Label>
                      <Input
                        value={editData.study}
                        onChange={(e) => setEditData({...editData, study: e.target.value})}
                        data-testid="edit-study"
                      />
                    </div>
                    
                    {/* Wohnort */}
                    <div className="space-y-2 pt-4 border-t border-slate-200">
                      <ResidenceLocationFields
                        value={editData}
                        onChange={(patch) => setEditData(prev => ({ ...prev, ...patch }))}
                        testIdPrefix="edit-residence"
                        compact
                        showAddress
                        showHeading
                      />
                    </div>

                    {/* Mobilität */}
                    <div className="space-y-2 pt-4 border-t border-slate-200">
                      <Label>Mobilität</Label>
                      <div className="flex gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editData.hasDriverLicense}
                            onChange={(e) => setEditData({...editData, hasDriverLicense: e.target.checked})}
                            className="w-4 h-4 rounded border-slate-300"
                          />
                          <span className="text-sm">Führerschein</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editData.hasOwnCar}
                            onChange={(e) => setEditData({...editData, hasOwnCar: e.target.checked})}
                            className="w-4 h-4 rounded border-slate-300"
                          />
                          <span className="text-sm">Eigener PKW</span>
                        </label>
                      </div>
                    </div>

                    {/* Verfügbarkeit */}
                    <div className="space-y-2 pt-4 border-t border-slate-200">
                      <Label>Verfügbarkeit</Label>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <Label className="text-xs text-slate-500">Stunden/Woche</Label>
                          <Input
                            type="number"
                            value={editData.availableHoursPerWeek}
                            onChange={(e) => setEditData({...editData, availableHoursPerWeek: parseInt(e.target.value) || ''})}
                            placeholder="z.B. 20"
                            data-testid="edit-hours"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-slate-500">Einschränkungen</Label>
                          <Select
                            value={editData.timeConstraints}
                            onValueChange={(value) => setEditData({...editData, timeConstraints: value})}
                          >
                            <SelectTrigger data-testid="edit-constraints">
                              <SelectValue placeholder="Wählen..." />
                            </SelectTrigger>
                            <SelectContent className="z-[100]">
                              <SelectItem value="Keine Einschränkungen">Keine Einschränkungen</SelectItem>
                              <SelectItem value="Nur Wochenende">Nur Wochenende</SelectItem>
                              <SelectItem value="Nur Vormittags">Nur Vormittags</SelectItem>
                              <SelectItem value="Nur Nachmittags">Nur Nachmittags</SelectItem>
                              <SelectItem value="Nur Abends">Nur Abends</SelectItem>
                              <SelectItem value="Flexible Zeiten">Flexible Zeiten</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>

                    {/* Sprachkenntnisse */}
                    <div className="space-y-2 pt-4 border-t border-slate-200">
                      <Label>Sprachkenntnisse</Label>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {editData.languageSkills.map((lang, idx) => (
                          <Badge key={idx} variant="secondary" className="flex items-center gap-1">
                            {lang.language} ({lang.level})
                            <button
                              onClick={() => setEditData({
                                ...editData,
                                languageSkills: editData.languageSkills.filter((_, i) => i !== idx)
                              })}
                              className="ml-1 hover:text-red-500"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <Input
                          value={newLanguage.language}
                          onChange={(e) => setNewLanguage({...newLanguage, language: e.target.value})}
                          placeholder="Sprache"
                          className="flex-1"
                        />
                        <Select
                          value={newLanguage.level}
                          onValueChange={(value) => setNewLanguage({...newLanguage, level: value})}
                        >
                          <SelectTrigger className="w-24">
                            <SelectValue placeholder="Level" />
                          </SelectTrigger>
                          <SelectContent className="z-[100]">
                            {['A1', 'A2', 'B1', 'B2', 'C1', 'C2'].map(level => (
                              <SelectItem key={level} value={level}>{level}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            if (newLanguage.language && newLanguage.level) {
                              setEditData({
                                ...editData,
                                languageSkills: [...editData.languageSkills, {...newLanguage}]
                              });
                              setNewLanguage({ language: '', level: '' });
                            }
                          }}
                        >
                          +
                        </Button>
                      </div>
                    </div>

                    {/* Bevorzugte Arbeitsregionen */}
                    <div className="space-y-2 pt-4 border-t border-slate-200">
                      <Label>Bevorzugte Arbeitsregionen</Label>
                      <PreferredWorkLocationsField
                        preferredStates={editData.preferredStates}
                        preferredWorkLocations={editData.preferredWorkLocations || []}
                        onToggleState={(state) => setEditData(prev => ({
                          ...prev,
                          preferredStates: prev.preferredStates.includes(state)
                            ? prev.preferredStates.filter(s => s !== state)
                            : [...prev.preferredStates, state]
                        }))}
                        onAddLocation={(loc) => setEditData(prev => {
                          const list = prev.preferredWorkLocations || [];
                          const exists = list.some(l => l.state === loc.state && l.district === loc.district);
                          return exists ? prev : { ...prev, preferredWorkLocations: [...list, loc] };
                        })}
                        onRemoveLocation={(idx) => setEditData(prev => ({
                          ...prev,
                          preferredWorkLocations: (prev.preferredWorkLocations || []).filter((_, i) => i !== idx)
                        }))}
                        variant="dashboard"
                        testIdPrefix="edit-region"
                      />
                    </div>
                    
                    {/* Job Categories */}
                    <div className="space-y-2 pt-4 border-t border-slate-200">
                      <Label className="flex items-center gap-2">
                        <Tag className="w-4 h-4" />
                        Bevorzugte Jobbereiche (Interessen)
                      </Label>
                      <JobAreasField
                        selected={editData.preferredJobAreas}
                        available={availableCategories}
                        onToggle={toggleJobArea}
                        loading={loadingCategories}
                        testIdPrefix="edit-job-area"
                        buttonSize="sm"
                        scrollable
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowEditProfile(false)}>Abbrechen</Button>
                    <Button onClick={handleSaveProfile} data-testid="save-profile">Speichern</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
            
            <Card className="border-slate-100 shadow-sm rounded-2xl">
              <CardContent className="p-6">
                {/* Profilbild mit Ändern-Button */}
                <div className="flex justify-center mb-6">
                  <div className="relative">
                    {user.profile?.profileImage ? (
                      <img
                        src={user.profile.profileImage}
                        alt="Profil"
                        className="w-24 h-24 rounded-full object-cover border-4 border-primary/20"
                      />
                    ) : (
                      <div className="w-24 h-24 rounded-full bg-slate-100 flex items-center justify-center border-4 border-primary/20">
                        <User className="w-10 h-10 text-slate-400" />
                      </div>
                    )}
                    <button
                      onClick={() => profileImageInputRef.current?.click()}
                      disabled={uploadingImage}
                      className="absolute bottom-0 right-0 bg-primary text-white p-2 rounded-full shadow-lg hover:bg-primary/90 transition-colors"
                      data-testid="change-profile-image-button"
                    >
                      {uploadingImage ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Camera className="w-4 h-4" />
                      )}
                    </button>
                    <input
                      ref={profileImageInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleProfileImageUpload}
                      className="hidden"
                      data-testid="profile-image-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm text-slate-500">Name</Label>
                    <p className="font-medium">
                      {user.profile?.firstName} {user.profile?.lastName}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm text-slate-500">Email</Label>
                    <p className="font-medium">{user.email}</p>
                  </div>
                  <div>
                    <Label className="text-sm text-slate-500">Telefon</Label>
                    <p className="font-medium">{user.profile?.phone}</p>
                  </div>
                  <div>
                    <Label className="text-sm text-slate-500">Studium</Label>
                    <p className="font-medium">{user.profile?.study}</p>
                  </div>
                  <div>
                    <Label className="text-sm text-slate-500">Wohnort</Label>
                    <p className="font-medium">
                      {user.profile?.residenceCity ? `${user.profile.residenceCity}, ` : ''}
                      {user.profile?.residenceState || 'Nicht angegeben'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm text-slate-500">Mobilität</Label>
                    <div className="flex gap-2 mt-1">
                      {user.profile?.hasDriverLicense && (
                        <Badge variant="outline">Führerschein</Badge>
                      )}
                      {user.profile?.hasOwnCar && (
                        <Badge variant="outline">Eigener PKW</Badge>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm text-slate-500">Bevorzugte Arbeitsregionen</Label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {user.profile?.preferredStates?.map(state => (
                        <Badge key={state} variant="secondary">{state}</Badge>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm text-slate-500">Bevorzugte Jobbereiche</Label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {user.profile?.preferredJobAreas?.map(area => (
                        <Badge key={area} className="bg-primary/10 text-primary">{area}</Badge>
                      ))}
                      {(!user.profile?.preferredJobAreas || user.profile.preferredJobAreas.length === 0) && (
                        <span className="text-sm text-slate-400">Keine Jobbereiche ausgewählt</span>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Konto löschen */}
            <Card className="border-red-200 bg-red-50/50 shadow-sm rounded-2xl">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-red-900 mb-2">Gefahrenzone</h3>
                <p className="text-sm text-red-700 mb-4">
                  Das Löschen deines Kontos ist dauerhaft und kann nicht rückgängig gemacht werden.
                </p>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="destructive"
                      className="rounded-full"
                      data-testid="delete-account-button"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Konto löschen
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Konto wirklich löschen?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Diese Aktion kann nicht rückgängig gemacht werden. Alle deine Daten und Bewerbungen werden dauerhaft gelöscht.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleDeleteAccount}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        Endgültig löschen
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Mobile Bottom Navigation */}
      {isMobile && (
        <div className="fixed bottom-0 left-0 right-0 bg-white/80 backdrop-blur-xl border-t border-slate-200 h-16 flex items-center justify-around z-50">
          <button
            onClick={() => setActiveTab('jobs')}
            className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
              activeTab === 'jobs' ? 'text-primary' : 'text-slate-400'
            }`}
            data-testid="mobile-tab-jobs"
          >
            <Home className="w-5 h-5 mb-1" />
            <span className="text-xs font-medium">Jobs</span>
          </button>
          <button
            onClick={() => setActiveTab('applications')}
            className={`flex flex-col items-center justify-center flex-1 h-full transition-colors relative ${
              activeTab === 'applications' ? 'text-primary' : 'text-slate-400'
            }`}
            data-testid="mobile-tab-applications"
          >
            <ClipboardList className="w-5 h-5 mb-1" />
            <span className="text-xs font-medium">Bewerbungen</span>
            {proposals.filter(p => p.status === 'pending').length > 0 && (
              <span className="absolute top-1 right-6 bg-amber-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {proposals.filter(p => p.status === 'pending').length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('profile')}
            className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
              activeTab === 'profile' ? 'text-primary' : 'text-slate-400'
            }`}
            data-testid="mobile-tab-profile"
          >
            <User className="w-5 h-5 mb-1" />
            <span className="text-xs font-medium">Profil</span>
          </button>
          <button
            onClick={onLogout}
            className="flex flex-col items-center justify-center flex-1 h-full transition-colors text-slate-400"
            data-testid="mobile-tab-logout"
          >
            <LogOut className="w-5 h-5 mb-1" />
            <span className="text-xs font-medium">Abmelden</span>
          </button>
        </div>
      )}
    </div>
  );
}
